package com.eladfinish.bible.contest.screens.randomVerse.ui

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.sp

@Composable
internal fun AutoResizeText(
    text: String,
    modifier: Modifier = Modifier,
    color: Color,
    fontWeight: FontWeight = FontWeight.Normal,
    textAlign: TextAlign,
    maxFontSize: Float = 16f,
    minFontSize: Float = 10f,
    hasUnderline: Boolean = false,
) {
    var fontSize by remember(text, maxFontSize, minFontSize) {
        mutableStateOf(maxFontSize)
    }

    var lowerBound by remember(text, maxFontSize, minFontSize) {
        mutableStateOf(minFontSize)
    }

    var upperBound by remember(text, maxFontSize, minFontSize) {
        mutableStateOf(maxFontSize)
    }

    var readyToDraw by remember(text, maxFontSize, minFontSize) {
        mutableStateOf(false)
    }

    Text(
        text = text,
        color = color,
        textAlign = textAlign,
        fontWeight = fontWeight,
        maxLines = 1,
        softWrap = false,
        onTextLayout = { result ->
            if (readyToDraw) return@Text

            if (result.hasVisualOverflow) {
                upperBound = fontSize
            } else {
                lowerBound = fontSize
            }

            val nextSize = (lowerBound + upperBound) / 2f

            if ((upperBound - lowerBound) <= 0.5f) {
                fontSize = lowerBound
                readyToDraw = true
            } else {
                fontSize = nextSize
            }
        },
        modifier = modifier.drawWithContent {
            if (readyToDraw) {
                drawContent()
            }
        },
        style = MaterialTheme.typography.titleMedium.copy(
            fontSize = fontSize.sp,
            textDecoration = if (hasUnderline) {
                TextDecoration.Underline
            } else {
                TextDecoration.None
            },
            textDirection = TextDirection.Content,
        ),
    )
}

@Preview(name = "Short text")
@Composable
private fun AutoResizeTextPreview_Short() {
    MaterialTheme {
        AutoResizeText(
            text = "Hello",
            modifier = Modifier.fillMaxWidth(),
            color = Color.Black,
            fontWeight = FontWeight.Normal,
            textAlign = TextAlign.Center,
            hasUnderline = false,
        )
    }
}

@Preview(name = "Long text shrink")
@Composable
private fun AutoResizeTextPreview_Long() {
    MaterialTheme {
        AutoResizeText(
            text = "This is a very very long text that should shrink to fit",
            modifier = Modifier.fillMaxWidth(),
            color = Color.Black,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center,
            hasUnderline = false,
        )
    }
}

@Preview(name = "Underline")
@Composable
private fun AutoResizeTextPreview_Underline() {
    MaterialTheme {
        AutoResizeText(
            text = "Underlined text example",
            modifier = Modifier.fillMaxWidth(),
            color = Color.Blue,
            fontWeight = FontWeight.Medium,
            textAlign = TextAlign.Center,
            hasUnderline = true,
        )
    }
}

@Preview(name = "RTL Hebrew")
@Composable
private fun AutoResizeTextPreview_Hebrew() {
    MaterialTheme {
        AutoResizeText(
            text = "בראשית ברא אלוהים את השמים ואת הארץ",
            modifier = Modifier.fillMaxWidth(),
            color = Color.Black,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Right,
            hasUnderline = false,
        )
    }
}

@Preview(name = "Different alignments")
@Composable
private fun AutoResizeTextPreview_Alignments() {
    MaterialTheme {
        Column {
            AutoResizeText(
                text = "Start",
                modifier = Modifier.fillMaxWidth(),
                color = Color.Black,
                fontWeight = FontWeight.Normal,
                textAlign = TextAlign.Start,
                hasUnderline = false,
            )
            AutoResizeText(
                text = "Center",
                modifier = Modifier.fillMaxWidth(),
                color = Color.Black,
                fontWeight = FontWeight.Normal,
                textAlign = TextAlign.Center,
                hasUnderline = false,
            )
            AutoResizeText(
                text = "End",
                modifier = Modifier.fillMaxWidth(),
                color = Color.Black,
                fontWeight = FontWeight.Normal,
                textAlign = TextAlign.End,
                hasUnderline = false,
            )
        }
    }
}

@Preview(name = "Min font reached")
@Composable
private fun AutoResizeTextPreview_MinFont() {
    MaterialTheme {
        AutoResizeText(
            text = "Extremely long text that will force the font size to reach the minimum limit set in the composable",
            modifier = Modifier.fillMaxWidth(),
            color = Color.Red,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center,
            maxFontSize = 20f,
            minFontSize = 8f,
            hasUnderline = false,
        )
    }
}
